import * as _ from 'lodash';
import * as LL from './graph';
import * as Code from './code';
import {ResolverContext} from './resolverContext';
import * as HL from './hl';
import * as Types from '../types';

export interface AutosuggestResult {
  newNode: HL.EditingNode;
  suggestionString: string;
}

// For a given node, return a list of suggested new
// nodes to swap it with.
async function autosuggestNodes(
  context: ResolverContext,
  node: HL.EditingNode,
  graph: HL.EditingNode,
  frame: Code.Frame
): Promise<HL.EditingNode[]> {
  let result: HL.EditingNode[] = [];
  const consumer = HL.findConsumingOp(node, graph);
  const consumingOp = consumer?.outputNode.fromOp;
  if (node.nodeType === 'const' || node.nodeType === 'void') {
    if (consumingOp != null) {
      const consumingOpDef = LL.getOpDef(consumingOp.name);
      const consumingOpInputTypes = Object.values(consumingOpDef.inputTypes);
      if (consumingOp.name === 'pick') {
        const pickObj = consumingOp.inputs.obj;
        const pickKeys = HL.pickSuggestions(pickObj.type);
        result = pickKeys.map(key => HL.constString(key));
      }
      if (consumingOp.name === 'root-project') {
        let keys: string[] = [];
        if (consumer?.argName === 'entityName') {
          keys = ['shawn', 'wandb'];
        } else if (consumer?.argName === 'projectName') {
          keys = ['fasion-sweep', 'dsviz_demo', 'dsviz-simple-tables'];
        }
        result = keys.map(key => {
          const varNode = HL.constString(key);
          return varNode;
        });
      } else if (
        consumingOp.name === 'string-equal' ||
        consumingOp.name === 'string-notequal'
      ) {
        // Assume this is rhs for now.
        if (consumer?.argName !== 'rhs') {
          throw new Error('invalid');
        }
        const lhsNode = consumingOp.inputs.lhs;
        if (lhsNode.nodeType === 'void') {
          throw new Error('invalid');
        }
        const called = HL.callFunction(lhsNode as any, frame);
        const generalizedNode = HL.mapNodes(called, curNode => {
          if (curNode.nodeType !== 'output') {
            return curNode;
          }
          const fromOp = curNode.fromOp;
          if (
            fromOp.name === 'filter' ||
            (fromOp.name === 'index' && fromOp.inputs.index.nodeType === 'var')
          ) {
            return fromOp.inputs.arr;
          }
          return curNode;
        });
        // const improvedNode = await LL.improveOutputNode(
        //   generalizedNode as any,
        //   context
        // );
        const possibleValues = (
          await LL.executeForwardFresh(context, [generalizedNode as any])
        )[0];
        const keys: string[] = _.uniq(possibleValues);
        result = keys.map(key => HL.constString(key));
        // do stuff
      } else if (
        HL.isBinaryOp(consumingOp) &&
        Types.isAssignableTo(consumingOpInputTypes[1], 'number')
      ) {
        result.push(HL.constNumber(3.14159));
      }
    }
    if (node.nodeType === 'void') {
      const variableNames = Object.keys(frame);
      // Don't allow root ops when there are variables present!
      // I expect we'll want to change this later.
      if (variableNames.length > 0) {
        for (const varName of variableNames) {
          // Recursively suggest results for each variable
          const varNode = LL.varNode(frame[varName].type, varName);
          const newGraph = HL.replaceNode(graph, node, varNode);
          const suggestNodes = await autosuggestNodes(
            context,
            varNode,
            newGraph,
            frame
          );
          result = result.concat(suggestNodes);
        }
      } else {
        result = result.concat(
          HL.rootOps().map(opDef => {
            const rootOpInputs = _.mapValues(opDef.inputTypes, v =>
              LL.voidNode()
            );
            return HL.callOp(opDef.name, rootOpInputs);
          })
        );
      }
    }
  } else if (node.nodeType === 'var' || node.nodeType === 'output') {
    const availOps = HL.availableOpsForChain(node.type);
    result = availOps.flatMap(opDef => {
      if (opDef.name === 'pick') {
        const pickKeys = HL.pickSuggestions(node.type);
        return pickKeys.map(key =>
          HL.callOp('pick', {obj: node, key: HL.constString(key)})
        );
      } else {
        const argNames = Object.keys(opDef.inputTypes);
        const opInputs: HL.EditingOpInputs = {
          [argNames[0]]: node,
        };
        for (const argName of argNames.slice(1)) {
          opInputs[argName] = LL.voidNode();
        }
        return HL.callOp(opDef.name, opInputs);
      }
    });
  }
  return result;
}

export async function autosuggest(
  context: ResolverContext,
  node: HL.EditingNode,
  graph: HL.EditingNode,
  frame: Code.Frame
): Promise<AutosuggestResult[]> {
  // NOTE: this function returns all valid nodes for the given position in the graph,
  // but does not filter them according to the currently entered text -- that happens
  // later, in WBSuggester.tsx

  const result = await autosuggestNodes(context, node, graph, frame);
  result.sort((a, b) => (HL.toString(a) < HL.toString(b) ? -1 : 1));

  return result.map(newNode => ({
    newNode,
    suggestionString: HL.toString(
      HL.maybeReplaceNode(newNode, node, LL.varNode('any', ''))
    ),
  }));
}
